import { useEffect, useId, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import ShipmentTracking from '../components/orders/ShipmentTracking.jsx'
import { apiErrorMessage, refreshIntervalFor } from '../components/orders/tracking/trackingUtils'

/**
 * Public order tracking (/track-order, tracking-plan §8.2) - no login.
 *  - Order number or AWB, plus the email or phone on the order (D4).
 *  - ?q=  prefills the order number / AWB.
 *  - ?t=  a signed tracking link from a shipping email - loads straight away.
 *  - ?ndr= a signed failed-delivery link - loads, and lets the customer reschedule.
 * The contact is never put in the URL.
 */

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null)

function lookup(request) {
  if (request.kind === 'ndr') return api.shipping.ndrByToken(request.token)
  if (request.kind === 'token') return api.shipping.track({ token: request.token })
  return api.shipping.track({ query: request.query, contact: request.contact })
}

export default function TrackOrder() {
  const [params, setParams] = useSearchParams()
  const linkToken = params.get('t')
  const ndrToken = params.get('ndr')
  const [form, setForm] = useState({ query: params.get('q') ?? '', contact: '' })
  const [errors, setErrors] = useState({})
  const [request, setRequest] = useState(() =>
    ndrToken ? { kind: 'ndr', token: ndrToken } : linkToken ? { kind: 'token', token: linkToken } : null,
  )

  useEffect(() => {
    document.title = 'Track your order'
  }, [])

  const { data, error, isFetching, isError, refetch } = useQuery({
    queryKey: ['publicTrack', request],
    queryFn: () => lookup(request),
    enabled: Boolean(request),
    retry: false,
    refetchInterval: (query) => refreshIntervalFor(query.state.data?.data?.shipment),
  })
  const result = data?.data ?? null

  const submit = (e) => {
    e.preventDefault()
    const query = form.query.trim()
    const contact = form.contact.trim()
    const next = {}
    if (query.length < 4) next.query = 'Enter your order number or AWB.'
    if (!contact.includes('@') && contact.replace(/\D/g, '').slice(-10).length !== 10) next.contact = 'Enter the email or 10-digit phone number used on the order.'
    setErrors(next)
    if (Object.keys(next).length > 0) return
    setRequest({ kind: 'query', query, contact })
    setParams(query ? { q: query } : {}, { replace: true })
  }

  const notFound = isError && error?.response?.status === 404
  const failure = isError
    ? notFound
      ? request?.kind === 'query'
        ? "We couldn't find that shipment. Check the order number or AWB, and use the same email or phone you ordered with."
        : 'This link has expired. Enter your order details below to track it.'
      : apiErrorMessage(error, "Tracking isn't available right now. Please try again in a few minutes.")
    : null

  return (
    <div className="min-h-screen bg-background font-normal uppercase select-none">
      <div className="container max-w-3xl px-4 sm:px-6 mx-auto pt-28 md:pt-32 pb-16 space-y-6">
        <header>
          <p className="text-xs text-secondary font-bold tracking-[0.3em]">Order Tracking</p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-heading text-primary">Track your order</h1>
          <p className="mt-3 text-sm text-primary/60 normal-case max-w-xl">
            Enter your order number (or the courier's AWB) and the email or phone number you used when ordering.
          </p>
        </header>

        <SearchForm form={form} setForm={setForm} errors={errors} onSubmit={submit} busy={isFetching && request?.kind === 'query'} />

        <div aria-live="polite" aria-busy={isFetching && !result}>
          {failure && <ErrorPanel message={failure} onRetry={notFound ? null : () => refetch()} />}
          {!failure && isFetching && !result && <ResultSkeleton />}
          {!failure && !request && <EmptyState />}
          {!failure && result && (
            <div className="space-y-6">
              <div className="bg-white rounded-3xl border border-border/40 shadow-sm px-6 sm:px-8 py-5 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs text-primary/40 tracking-widest">Order</p>
                  <p className="text-xl font-mono text-secondary normal-case">#{result.order_number}</p>
                </div>
                {result.ordered_at && <p className="text-sm text-primary/60 tracking-wider">Placed {fmtDate(result.ordered_at)}</p>}
              </div>
              {result.shipment || ['pending', 'confirmed', 'processing'].includes(String(result.order_status).toLowerCase()) ? (
                <ShipmentTracking
                  shipment={result.shipment}
                  orderStatus={result.order_status}
                  withStepper
                  onNdrRespond={ndrToken ? (body) => api.shipping.ndrRespondByToken(ndrToken, body) : undefined}
                  onRefresh={() => refetch()}
                />
              ) : (
                <p className="bg-white rounded-3xl border border-border/40 p-6 sm:p-8 text-sm text-primary/60 normal-case">
                  This order is {String(result.order_status).toLowerCase()}. For help, contact us from your order page.
                </p>
              )}
              <p className="text-center text-xs text-primary/50 normal-case">
                Have an account? <Link to={`/orders/${encodeURIComponent(result.order_number ?? '')}`} className="text-secondary font-semibold hover:underline">See the full order</Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function SearchForm({ form, setForm, errors, onSubmit, busy }) {
  const id = useId()
  const input =
    'w-full h-12 px-4 rounded-xl border bg-white text-sm text-primary normal-case focus:outline-none focus:border-secondary focus-visible:ring-2 focus-visible:ring-secondary/20'
  const field = (key, label, props) => (
    <div className="min-w-0">
      <label htmlFor={`${id}-${key}`} className="block text-xs text-primary/60 tracking-wider mb-1.5">
        {label}
      </label>
      <input
        id={`${id}-${key}`}
        value={form[key]}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        aria-invalid={Boolean(errors[key])}
        aria-describedby={errors[key] ? `${id}-${key}-error` : undefined}
        className={`${input} ${errors[key] ? 'border-red-400' : 'border-border'}`}
        {...props}
      />
      {errors[key] && (
        <p id={`${id}-${key}-error`} className="mt-1 text-xs text-red-600 normal-case">
          {errors[key]}
        </p>
      )}
    </div>
  )
  return (
    <form onSubmit={onSubmit} noValidate className="bg-white rounded-3xl border border-border/40 shadow-sm p-6 sm:p-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {field('query', 'Order number or AWB', { autoComplete: 'off', placeholder: 'e.g. ORD-ABC123', maxLength: 60 })}
        {field('contact', 'Email or phone on the order', { autoComplete: 'email', placeholder: 'you@example.com or 98765 43210', maxLength: 120 })}
      </div>
      <button
        type="submit"
        disabled={busy}
        className="mt-5 w-full sm:w-auto inline-flex items-center justify-center gap-2 h-12 px-8 rounded-xl bg-primary text-white text-xs font-bold tracking-[0.2em] hover:bg-secondary disabled:opacity-60 transition-all"
      >
        <i className={`fa-solid ${busy ? 'fa-spinner fa-spin' : 'fa-magnifying-glass'}`} aria-hidden="true"></i>
        {busy ? 'Tracking…' : 'Track'}
      </button>
    </form>
  )
}

function EmptyState() {
  return (
    <div className="text-center py-10">
      <i className="fa-solid fa-truck-fast text-4xl text-primary/15" aria-hidden="true"></i>
      <p className="mt-4 text-sm text-primary/50 normal-case">Your parcel's journey will appear here.</p>
    </div>
  )
}

function ErrorPanel({ message, onRetry }) {
  return (
    <div role="alert" className="bg-white rounded-3xl border border-red-200 p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-4 normal-case">
      <i className="fa-solid fa-circle-exclamation text-2xl text-red-400" aria-hidden="true"></i>
      <p className="text-sm text-primary/80 grow">{message}</p>
      {onRetry && (
        <button type="button" onClick={onRetry} className="h-11 px-6 rounded-xl border border-primary/25 text-primary text-xs font-bold tracking-[0.2em] uppercase hover:border-secondary hover:text-secondary shrink-0">
          Try again
        </button>
      )}
    </div>
  )
}

function ResultSkeleton() {
  return (
    <div>
      <span className="sr-only">Loading tracking…</span>
      <div className="space-y-6 animate-pulse" aria-hidden="true">
      <div className="h-20 rounded-3xl bg-white border border-border/40"></div>
      <div className="rounded-3xl bg-white border border-border/40 p-8 space-y-6">
        <div className="h-5 w-48 rounded bg-primary/10"></div>
        <div className="flex gap-4">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-8 w-8 rounded-full bg-primary/10"></div>
          ))}
        </div>
        <div className="h-4 w-2/3 rounded bg-primary/10"></div>
        <div className="h-4 w-1/2 rounded bg-primary/10"></div>
      </div>
      </div>
    </div>
  )
}
